import { useEffect, useMemo, useState } from 'react';
import { getUserByUuid } from '../../../services/index.js';
import { getViewerUuid } from '../utils/taskOwner.js';
import { MOCK_CURRENT_PERSONA } from '../constants/people.js';

/** Two-letter initials from a full name. */
function initialsOf(name = '') {
  return name
    .split(/\s+/)
    .map((part) => part[0] ?? '')
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

/**
 * The logged-in user as the work-queue persona (CT-5094, punto 4) — replaces
 * MOCK_CURRENT_PERSONA for identity fields.
 *
 *  - `id`       → viewer uuid from the token (`getViewerUuid`).
 *  - `name`     → resolved via `getUserByUuid` (cached; in My Work the viewer is
 *                 usually already cached from the inbox owner resolution). Falls
 *                 back to the mock name only until it resolves, or when there is
 *                 no session.
 *  - `initials` → derived from the resolved name.
 *
 * `role` / `studio` are STILL MOCK: the backend exposes no HTL role/studio for
 * the current user yet (its `role/list_filter` returns permission roles, not the
 * HTL manager/individual + studio the UI needs). Tracked as **CT-5094 punto 5**.
 * They stay as a fallback so the question-role label (`question.roles.<role>`)
 * and the My Team scope gate (`role === 'individual'`) keep working.
 */
export function useCurrentPersona() {
  const id = getViewerUuid();
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    if (!id) return undefined;
    let alive = true;
    getUserByUuid(id).then((user) => {
      if (alive && user?.name) setProfile({ name: user.name, initials: initialsOf(user.name) });
    });
    return () => {
      alive = false;
    };
  }, [id]);

  return useMemo(
    () => ({
      id: id ?? MOCK_CURRENT_PERSONA.id,
      name: profile?.name ?? MOCK_CURRENT_PERSONA.name,
      initials: profile?.initials ?? MOCK_CURRENT_PERSONA.initials,
      // TODO(CT-5094 punto 5): no HTL role/studio for the viewer from the backend
      // yet — kept mock so role-label + My Team gate keep working.
      role: MOCK_CURRENT_PERSONA.role,
      studio: MOCK_CURRENT_PERSONA.studio,
    }),
    [id, profile],
  );
}
